import {
  FormFieldAuthors,
  FormFieldContactAuthor,
  FormFieldCorrespondingAuthor,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { Badge, ButtonIcon, OutlineCard } from '@morressier/alembic';
import {
  groupAuthorsByRoles,
  IAuthorRolesMap
} from 'src/utils/groupAuthorByRoles';
import { IPeerReviewManagementAuthorView } from '@publishing-engine/domain/views';
import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import RinggoldBadge from 'src/components/RinggoldBadge';
import Divider from 'src/components/Divider';
import AuthorDetailsModal from '../../AuthorsTab/AuthorDetailsModal';
import {
  IAuthorWithContribution,
  IAuthorWithEmail
} from '@publishing-engine/domain/models';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import UserName from 'src/components/User/UserName';
import { OrcidIDIcon } from 'src/components/User/RenderSectionDetails';

const ReviewerInfo = () => {
  const { getFieldState } = useSubmissionFields('review');
  const authorsField = getFieldState<FormFieldAuthors>('Authors');
  const submittingAuthorEmail =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')?.value!;
  const contactAuthorEmail =
    getFieldState<FormFieldContactAuthor>('ContactAuthor')?.value;
  const correspondingAuthorEmail = getFieldState<FormFieldCorrespondingAuthor>(
    'CorrespondingAuthor'
  )?.value;
  const roles: IAuthorRolesMap = {
    Submitting: submittingAuthorEmail,
    ...(contactAuthorEmail && {
      Contact: contactAuthorEmail
    }),
    ...(correspondingAuthorEmail && {
      Corresponding: correspondingAuthorEmail
    })
  };
  const [selectedAuthor, setSelectedAuthor] =
    React.useState<IPeerReviewManagementAuthorView | null>(null);

  const renderAuthorTableRow = (
    author: IAuthorWithContribution,
    index: number
  ) => {
    return (
      <Tr
        key={author.id || index}
        data-testId="author-table-row"
        gap={4}
        background="white"
        _last={{ borderBottom: 'none' }}
      >
        <Td ps={1}>{index + 1}</Td>
        <Td
          ps={1}
          cursor="pointer"
          _hover={{ bg: 'gray.50' }}
          onClick={() => setSelectedAuthor({ ...author })}
        >
          <Flex direction={'column'}>
            {author?.firstName || author?.lastName ? (
              <UserName
                firstName={author.firstName}
                middleName={author.middleName}
                lastName={author.lastName}
              />
            ) : (
              <Text
                color="actionSecondaryCautionContent"
                textStyle="actionLabel"
              >
                No Name Data
              </Text>
            )}

            {author?.email ? (
              <Text
                textStyle="textContext"
                wordBreak="break-word"
                whiteSpace="normal"
              >
                {author?.email}
              </Text>
            ) : (
              <Text
                color="actionSecondaryCautionContent"
                textStyle="textContext"
              >
                No Email Data
              </Text>
            )}

            <>
              {author.orcid ? (
                <Flex align="flex-start" mt={1} gap={1}>
                  <Box flexShrink={0}>
                    <OrcidIDIcon value={author.orcid} />
                  </Box>
                  <Text textStyle="textContext">{author.orcid}</Text>
                </Flex>
              ) : (
                <Text textStyle="textContext" color="GrayText">
                  No ORCID provided
                </Text>
              )}
            </>
          </Flex>
        </Td>

        <Td ps={0}>
          <Flex align="flex-start" gap={2}>
            {author?.ringgoldId && <RinggoldBadge />}
            {author?.affiliation ? (
              <Text textStyle="textContext" data-cy="author-affiliation">
                {author.affiliation}
                {author.city && `, ${author.city}`}
                {author.country && `, ${author.country}`}
                {author.postalCode && `, ${author.postalCode}`}
              </Text>
            ) : (
              <Text
                color={'actionSecondaryCautionContent'}
                textStyle="textContext"
              >
                No Affiliation Data
              </Text>
            )}
          </Flex>
        </Td>

        <Td ps={0}>
          <ContributionsBadge contributions={author.contributions} />
        </Td>
      </Tr>
    );
  };

  const AuthorCard = ({
    author,
    roles
  }: {
    author: IPeerReviewManagementAuthorView;
    roles: string[];
  }) => {
    return (
      <>
        {/* Corresponding Author */}
        <Flex
          flexDirection="column"
          padding={4}
          gap={4}
          bgColor="surfacePrimaryFill"
        >
          {roles.length && (
            <>
              <Flex gap={4}>
                {roles?.map(role => (
                  <Badge variant="attention" size="context">
                    {role} Author
                  </Badge>
                ))}
              </Flex>
              {Divider}
            </>
          )}
          {/* Avatar */}
          <Flex gap={4}>
            <Flex flexDirection="column" flex={1}>
              <Text textStyle="headingItem">
                {author.firstName} {author.lastName}
              </Text>
              <Text textStyle="textBody">
                {author.affiliation}
                {author?.country && `, ${author?.country}`}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  };

  const renderAuthors = () =>
    groupAuthorsByRoles(roles, authorsField?.value).map(author => (
      <AuthorCard key={author.email} author={author} roles={author.roles} />
    ));

  return (
    <>
      {selectedAuthor && (
        <AuthorDetailsModal
          author={selectedAuthor as IAuthorWithEmail}
          onBackClick={() => setSelectedAuthor(null)}
        />
      )}

      <Flex gap={8} flexDirection="column">
        <Flex gap={3} flexDirection="column">
          <Text textStyle="headingGroup">Author Roles</Text>
          <OutlineCard> {renderAuthors()}</OutlineCard>
        </Flex>

        {authorsField?.value && authorsField.value.length > 0 ? (
          <>
            {/* Authors List */}
            <Text textStyle="headingGroup">
              All Authors ({authorsField.value.length})
            </Text>
            <Box overflowY={'auto'} width="100%">
              <Table size={'sm'} minWidth="100%">
                <Thead>
                  <Tr
                    data-testId="author-table-header"
                    gap={4}
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        background: 'white'
                      }
                    }}
                  >
                    <Th ps={1}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        S.No.
                      </Text>
                    </Th>
                    <Th w={'30%'} ps={1}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Author
                      </Text>
                    </Th>

                    <Th ps={0}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Affiliation
                      </Text>
                    </Th>

                    <Th ps={0}>
                      <Flex flex={1} alignItems="center" display={'flex'}>
                        <Text textStyle="headingItem" whiteSpace={'nowrap'}>
                          CRediT Contributions
                          <Tooltip
                            hasArrow
                            shouldWrapChildren
                            placement="right"
                            label="Contributor Role Taxonomy (CRediT) Contributions"
                            borderRadius={4}
                            data-cy="contributions-info-tooltip"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Back to workspace settings"
                              icon={<FontAwesomeIcon icon={faInfoCircle} />}
                              data-cy="contributions-info-icon"
                            />
                          </Tooltip>
                        </Text>
                      </Flex>
                    </Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {authorsField?.value.map((author, index) => (
                    <React.Fragment key={index}>
                      {renderAuthorTableRow(author, index)}
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </>
        ) : null}
      </Flex>
    </>
  );
};

export default ReviewerInfo;
